// ============================================================
// phases.ts — 人生フェーズ（life_story_user_stages）共通レイヤー
// ============================================================
//
// 日記分析を「人生フェーズ」（生活構造が一貫している期間）単位で行う
// ための共通ヘルパー。求職中・旅行中・仕事中・学生では曜日の意味も
// 生活リズムも違うので、フェーズ相対で分析する。
//
// データ元: life_story_user_stages（migration 080 で date_start /
// date_end / context_tags / source / summary を追加済）。
// preset 行（幼少期…最近、年精度の year_start のみ）は分析スコープには
// 使わないので fetchPhases() の結果からは除外する（Roots の年表表示は
// LifeHistory.tsx が別途取得している）。
// ============================================================

import { supabase } from '@/lib/supabase'

export type PhaseKind = 'preset' | 'custom'
export type PhaseSource = 'preset' | 'manual' | 'detected'

export interface LifePhase {
  id: string
  key: string
  label: string
  kind: PhaseKind
  source: PhaseSource
  /** 'YYYY-MM-DD' */
  dateStart: string | null
  /** 'YYYY-MM-DD'。null = 現在進行中 */
  dateEnd: string | null
  contextTags: string[]
  summary: string | null
  sortOrder: number
}

/** context_tags の候補（日本語ラベル併記）。Settings の編集UIで使う。 */
export const PHASE_CONTEXT_TAG_OPTIONS: { value: string; label: string }[] = [
  { value: 'employed', label: '会社員' },
  { value: 'freelance', label: '業務委託' },
  { value: 'job_hunting', label: '求職中' },
  { value: 'traveling', label: '旅行中' },
  { value: 'student', label: '学生' },
  { value: 'unemployed', label: '無職' },
  { value: 'sabbatical', label: '休職' },
]

/** context_tag を日本語ラベルにする（未知のタグはそのまま返す）。 */
export function contextTagLabel(tag: string): string {
  return PHASE_CONTEXT_TAG_OPTIONS.find((o) => o.value === tag)?.label ?? tag
}

interface PhaseRow {
  id: string
  key: string
  label: string
  kind: PhaseKind | null
  source: PhaseSource | null
  date_start: string | null
  date_end: string | null
  context_tags: string[] | null
  summary: string | null
  sort_order: number | null
}

function rowToPhase(r: PhaseRow): LifePhase {
  return {
    id: r.id,
    key: r.key,
    label: r.label,
    kind: (r.kind ?? 'custom') as PhaseKind,
    source: (r.source ?? 'manual') as PhaseSource,
    dateStart: r.date_start,
    dateEnd: r.date_end,
    contextTags: r.context_tags ?? [],
    summary: r.summary,
    sortOrder: r.sort_order ?? 0,
  }
}

/**
 * 分析スコープに使えるフェーズ（date_start を持つもの）を、
 * sort_order → date_start の順で返す。
 * date_start が null の preset 行（年表の骨格）は除外する。
 */
export async function fetchPhases(): Promise<LifePhase[]> {
  const { data, error } = await supabase
    .from('life_story_user_stages')
    .select('id, key, label, kind, source, date_start, date_end, context_tags, summary, sort_order')
    .not('date_start', 'is', null)
  if (error) {
    console.warn('[phases] fetchPhases failed:', error)
    return []
  }
  const phases = (data as PhaseRow[]).map(rowToPhase)
  phases.sort((a, b) => {
    const so = a.sortOrder - b.sortOrder
    if (so !== 0) return so
    return (a.dateStart ?? '').localeCompare(b.dateStart ?? '')
  })
  return phases
}

/** date('YYYY-MM-DD') がどのフェーズに属するかを返す（重複時は dateStart の遅い方を優先）。 */
export function phaseForDate(date: string, phases: LifePhase[]): LifePhase | null {
  const candidates = phases.filter((p) => {
    if (!p.dateStart) return false
    if (date < p.dateStart) return false
    if (p.dateEnd && date > p.dateEnd) return false
    return true
  })
  if (candidates.length === 0) return null
  candidates.sort((a, b) => (b.dateStart ?? '').localeCompare(a.dateStart ?? ''))
  return candidates[0]
}

/** 現在進行中のフェーズ（dateEnd が null、または今日を含むもの）を返す。 */
export function currentPhase(phases: LifePhase[]): LifePhase | null {
  const ongoing = phases
    .filter((p) => p.dateStart && p.dateEnd === null)
    .sort((a, b) => (b.dateStart ?? '').localeCompare(a.dateStart ?? ''))
  if (ongoing.length > 0) return ongoing[0]
  const today = new Date().toISOString().substring(0, 10)
  return phaseForDate(today, phases)
}
